import type { ValuesPayload, ZAnyConfig } from '@atlaskit/config-common-libs';
import type { CustomAttributes, Identifiers } from '@atlaskit/feature-gate-js-client';
import FeatureGates, { PerimeterType } from '@atlaskit/feature-gate-js-client';

import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import type { SessionDataType } from '@confluence/session-data/entry-points/SessionDataTypes.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import { getStatsigEnvironment } from './getStatsigEnvironment.ts';

// https://bitbucket.org/atlassian/feature-flag-service/src/a6adccc26a95818c8128c63e4eb817414793b3c2/src/main/resources/application.yml#lines-386
const TARGET_APP = 'confluence_web';

export async function initializeClientFromValues({
	identifiers,
	environment: confluenceEnvironment,
	clientSdkKey,
	experimentValues,
	customAttributes,
	configCollectionValues,
	localMode,
}: {
	identifiers: {
		userId: SessionDataType['userId'];
		tenantId: Identifiers['tenantId'];
		orgId?: Identifiers['atlassianOrgId'];
	};
	environment: SessionDataType['environment'] | string;
	clientSdkKey?: string;
	experimentValues?: Record<string, unknown>;
	customAttributes?: CustomAttributes;
	configCollectionValues?: ValuesPayload | ZAnyConfig;
	localMode?: boolean;
}) {
	if (FeatureGates.initializeCompleted()) {
		return;
	}

	const { userId, tenantId, orgId } = identifiers;
	try {
		await FeatureGates.initializeFromValues(
			{
				sdkKey: clientSdkKey,
				environment: getStatsigEnvironment(confluenceEnvironment),
				targetApp: TARGET_APP,
				perimeter:
					process.env.MICROS_PERIMETER === 'fedramp-moderate'
						? PerimeterType.FEDRAMP_MODERATE
						: PerimeterType.COMMERCIAL,
				...(localMode ? { localMode: true } : {}),
			},
			{
				atlassianAccountId: userId || undefined,
				tenantId,
				atlassianOrgId: orgId,
			},
			customAttributes,
			localMode ? {} : experimentValues,
		);

		StatsigConfigurations.initializeFromValues(configCollectionValues || {});
	} catch (error: unknown) {
		if (error instanceof Error) {
			getMonitoringClient().submitError(error, {
				attribution: 'statsig',
			});
		}
	}
}
